import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ProductGrid } from '../components/ProductGrid';
import { ProductFilters } from '../components/ProductFilters';
import { productsService } from '../services/products';
import { categoriesService } from '../services/categories';
import { ProductQueryParams } from '../types/woocommerce';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  
  const [filters, setFilters] = useState<ProductQueryParams>(() => {
    const params: ProductQueryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      per_page: 12,
    };

    // Récupérer les filtres depuis l'URL
    if (searchParams.get('orderby')) params.orderby = searchParams.get('orderby')!;
    if (searchParams.get('order')) params.order = searchParams.get('order') as 'asc' | 'desc';
    if (searchParams.get('category')) params.category = searchParams.get('category')!;
    if (searchParams.get('min_price')) params.min_price = searchParams.get('min_price')!;
    if (searchParams.get('max_price')) params.max_price = searchParams.get('max_price')!;
    if (searchParams.get('on_sale')) params.on_sale = searchParams.get('on_sale') === 'true';
    if (searchParams.get('featured')) params.featured = searchParams.get('featured') === 'true';
    if (searchParams.get('stock_status')) params.stock_status = searchParams.get('stock_status') as any;

    return params;
  });

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Récupérer les produits
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products', 'search', searchQuery, filters],
    queryFn: () => productsService.searchProducts(searchQuery, filters),
    enabled: !!searchQuery,
  });

  // Récupérer les catégories pour les filtres
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => categoriesService.getCategories({ per_page: 100, hide_empty: true }),
  });

  // Mettre à jour l'URL quand les filtres changent
  useEffect(() => {
    const params = new URLSearchParams();
    
    params.set('q', searchQuery);
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });

    setSearchParams(params);
  }, [filters, searchQuery, setSearchParams]);

  const handleFiltersChange = (newFilters: ProductQueryParams) => {
    setFilters({ ...newFilters, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(localSearchQuery.trim())}`;
    }
  };

  // Suggestions de recherche basées sur les résultats
  const getSuggestions = () => {
    if (!productsData || productsData.products.length > 5) return null;
    
    const categories = productsData.products.flatMap(p => p.categories);
    const uniqueCategories = Array.from(new Map(categories.map(c => [c.id, c])).values());
    
    const tags = productsData.products.flatMap(p => p.tags);
    const uniqueTags = Array.from(new Map(tags.map(t => [t.id, t])).values());
    
    return { categories: uniqueCategories, tags: uniqueTags };
  };

  const suggestions = getSuggestions();

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-custom py-8">
        {/* Barre de recherche principale */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                placeholder="Rechercher des produits..."
                className="w-full pl-12 pr-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Rechercher
              </button>
            </div>
          </form>
        </div>

        {/* Résultats de recherche */}
        {searchQuery ? (
          <>
            <h1 className="text-2xl font-bold mb-6">
              Résultats de recherche pour "{searchQuery}"
              {productsData && (
                <span className="text-gray-500 font-normal ml-2">
                  ({productsData.pagination.total} résultat{productsData.pagination.total > 1 ? 's' : ''})
                </span>
              )}
            </h1>

            {/* Suggestions si peu de résultats */}
            {suggestions && productsData && productsData.products.length < 5 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800 mb-2">
                  Peu de résultats trouvés. Vous pourriez aussi rechercher dans :
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.categories.length > 0 && (
                    <>
                      <span className="text-sm text-gray-600">Catégories :</span>
                      {suggestions.categories.map(cat => (
                        <a
                          key={cat.id}
                          href={`/category/${cat.slug}`}
                          className="text-sm bg-white px-3 py-1 rounded-full border border-blue-300 hover:border-blue-500 hover:text-blue-600 transition-colors"
                        >
                          {cat.name}
                        </a>
                      ))}
                    </>
                  )}
                  {suggestions.tags.length > 0 && (
                    <>
                      <span className="text-sm text-gray-600 ml-4">Tags :</span>
                      {suggestions.tags.map(tag => (
                        <button
                          key={tag.id}
                          onClick={() => window.location.href = `/search?q=${encodeURIComponent(tag.name)}`}
                          className="text-sm bg-white px-3 py-1 rounded-full border border-blue-300 hover:border-blue-500 hover:text-blue-600 transition-colors"
                        >
                          {tag.name}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="lg:grid lg:grid-cols-4 lg:gap-8">
              {/* Filtres */}
              <aside className="mb-8 lg:mb-0">
                <ProductFilters
                  categories={categoriesData?.categories || []}
                  currentFilters={filters}
                  onFiltersChange={handleFiltersChange}
                />
              </aside>

              {/* Grille de produits */}
              <main className="lg:col-span-3">
                {/* Résumé des résultats */}
                {productsData && productsData.products.length > 0 && (
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-gray-700">
                      Affichage de{' '}
                      <span className="font-medium">
                        {(filters.page! - 1) * filters.per_page! + 1}
                      </span>{' '}
                      à{' '}
                      <span className="font-medium">
                        {Math.min(filters.page! * filters.per_page!, productsData.pagination.total)}
                      </span>{' '}
                      sur{' '}
                      <span className="font-medium">{productsData.pagination.total}</span>{' '}
                      résultats
                    </p>
                  </div>
                )}

                <ProductGrid
                  products={productsData?.products || []}
                  loading={productsLoading}
                  error={productsError as Error}
                  emptyMessage={`Aucun produit trouvé pour "${searchQuery}"`}
                />

                {/* Pagination */}
                {productsData && productsData.pagination.totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(filters.page! - 1)}
                        disabled={filters.page === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>

                      {/* Numéros de page */}
                      <div className="flex gap-1">
                        {[...Array(Math.min(5, productsData.pagination.totalPages))].map((_, idx) => {
                          let pageNum: number;
                          if (productsData.pagination.totalPages <= 5) {
                            pageNum = idx + 1;
                          } else if (filters.page! <= 3) {
                            pageNum = idx + 1;
                          } else if (filters.page! >= productsData.pagination.totalPages - 2) {
                            pageNum = productsData.pagination.totalPages - 4 + idx;
                          } else {
                            pageNum = filters.page! - 2 + idx;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-md ${
                                pageNum === filters.page
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(filters.page! + 1)}
                        disabled={filters.page === productsData.pagination.totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </nav>
                  </div>
                )}
              </main>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">
              Recherchez des produits
            </h2>
            <p className="text-gray-500">
              Utilisez la barre de recherche ci-dessus pour trouver ce que vous cherchez
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

